/**
 * Serialized trace value shapes produced by the rules engine serializer.
 *
 * The serializer emits one special envelope shape inside trace values:
 * - `{ _truncated: true, ... }` — placeholder for a value too large to store.
 *
 * Any other value renders as plain JSON (fallback).
 */

export type TruncatedEnvelope = {
  _truncated: true;
  circular?: true;
  type: string;
  size: number;
  preview: string;
  keys?: string[];
  length?: number;
};

/**
 * Type-guard: value is a truncation envelope.
 */
export const isTruncated = ( v: unknown ): v is TruncatedEnvelope => {
  if ( !v || typeof v !== 'object' || Array.isArray( v )) return false;
  const obj = v as Record<string, unknown>;
  return obj._truncated === true;
};

/**
 * Format bytes (from JSON.stringify length) as a human string.
 */
export const formatBytes = ( n: number ): string => {
  if ( !Number.isFinite( n ) || n < 0 ) return '?';
  if ( n < 1024 ) return `${n} B`;
  if ( n < 1024 * 1024 ) return `${( n / 1024 ).toFixed( 1 )} KB`;
  return `${( n / ( 1024 * 1024 )).toFixed( 2 )} MB`;
};

/**
 * Translatable labels for trace viewer UI.
 *
 * This package is i18n-library-agnostic. Consumers (e.g. the web app) translate
 * these strings with their own i18n tool and pass them in via props. Defaults
 * are in Spanish to preserve the original hardcoded behaviour when the prop is
 * omitted.
 */
export type TraceLabels = {
  fullValueNotStored: string;
  /** Empty-state label shown by `NodeOutputsPanel` when no nodes executed. */
  noNodesExecuted?: string;
  /** Badge shown in the header of a node whose trace status is `'error'`. */
  nodeFailedBadge?: string;
  /**
   * Banner shown above the editor when a rule execution failed. Supports the
   * `{{nodeId}}` placeholder which will be substituted with the failed node id.
   */
  executionFailedBanner?: string;
  /** Label for the banner action that centres the canvas on the failed node. */
  jumpToFailedNode?: string;
  /** Translated name for the `'transient'` error type shown in the failure banner. */
  errorTypeTransient?: string;
  /** Translated name for the `'permanent'` error type shown in the failure banner. */
  errorTypePermanent?: string;
  /** Prefix shown above the error message surfaced inside a failed node. */
  errorMessagePrefix?: string;
};

export const DEFAULT_TRACE_LABELS: TraceLabels = {
  fullValueNotStored: 'Valor completo no almacenado en la traza.',
  noNodesExecuted: 'Sin nodos ejecutados',
  nodeFailedBadge: 'Error',
  executionFailedBanner: 'Ejecución de la regla falló en el nodo `{{nodeId}}`',
  jumpToFailedNode: 'Ir al nodo que falló',
  errorTypeTransient: 'transitorio',
  errorTypePermanent: 'permanente',
  errorMessagePrefix: 'Mensaje de error'
};
